import { create } from 'zustand';
import type {
  Conflict, Participant, SessionState, TelemetryReading,
} from '@micplot/shared';
import { api, loadIdentity, saveIdentity, type AuthUser, type Identity } from '../lib/api';

export type ViewName = 'a1' | 'a2' | 'timeline' | 'stage' | 'cues';

interface Store {
  /** The signed-in producer, if any. Crew have no account and stay null. */
  user: AuthUser | null;
  /** True until the first /auth/me call settles, so we don't flash the login screen. */
  authChecked: boolean;
  identity: Identity | null;
  state: SessionState | null;
  conflicts: Conflict[];
  /** Live RF readings from the venue bridge, if one is running. */
  telemetry: TelemetryReading[];
  rfLive: boolean;
  /** serverNow minus this browser's clock, so reading ages stay honest. */
  clockSkewMs: number;
  /**
   * Whether a bridge has ever fed this session.
   *
   * Losing the feed and never having had one look identical on screen -- no
   * meters, no alerts -- so without this the app cannot tell you it has gone
   * blind, which is the one thing it must never fail to say.
   */
  rfEverLive: boolean;
  /** Per-channel history, for the alerts that are patterns rather than readings. */
  rfHistory: Record<string, {
    samples: number; dropouts: number; antennaA: number; antennaB: number;
    minRfLevel?: number; minBatteryMinutes?: number;
  }>;
  connected: boolean;
  view: ViewName;
  /*
   * Where *this* position is looking in the running order.
   *
   * Deliberately not on the server. Looking ahead at the next number is
   * personal -- an A2 checking what is coming should not drag A1's board with
   * them -- while the plan and the live board stay shared. So only the gaze
   * is per-client; everything it can change is still everyone's.
   */
  following: boolean;
  previewPos: number;
  joinRequests: Participant[];
  toast: { text: string; kind: 'info' | 'error' } | null;

  setUser: (u: AuthUser | null) => void;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  setIdentity: (id: Identity | null) => void;
  setView: (v: ViewName) => void;
  setFollowing: (v: boolean) => void;
  setPreviewPos: (n: number) => void;
  notify: (text: string, kind?: 'info' | 'error') => void;
  dismissJoinRequest: (id: string) => void;
  connect: () => void;
}

/** Everything that describes one show and must not outlive it. */
const FRESH_SHOW = {
  telemetry: [] as TelemetryReading[],
  rfLive: false,
  rfEverLive: false,
  rfHistory: {},
  clockSkewMs: 0,
  conflicts: [] as Conflict[],
  following: true,
  previewPos: 0,
} as const;

let socket: WebSocket | null = null;
/** Which session the live socket belongs to, so we can tell a duplicate
 *  connect attempt from a genuine switch to another show. */
let socketKey = '';
let retry = 0;
let retryTimer: number | undefined;

export const useStore = create<Store>((set, get) => ({
  user: null,
  authChecked: false,
  identity: loadIdentity(),
  state: null,
  conflicts: [],
  telemetry: [],
  rfLive: false,
  clockSkewMs: 0,
  rfEverLive: false,
  rfHistory: {},
  connected: false,
  view: 'a2',
  following: true,
  previewPos: 0,
  joinRequests: [],
  toast: null,

  setUser: (user) => set({ user }),

  // Detaching starts from wherever the show is, so the first Next is a step
  // forward from here rather than a jump to somewhere unrelated.
  setFollowing: (following) => set(st => ({
    following,
    previewPos: following ? st.previewPos : (st.state?.session.playheadPos ?? 0),
  })),
  setPreviewPos: (previewPos) => set({ previewPos: Math.max(0, previewPos) }),

  refreshAuth: async () => {
    try {
      const r = await api.me();
      set({ user: r.user, authChecked: true });
    } catch {
      set({ user: null, authChecked: true });
    }
  },

  signOut: async () => {
    try { await api.logout(); } catch { /* the cookie is gone either way */ }
    saveIdentity(null);
    set({ user: null, identity: null, state: null, ...FRESH_SHOW });
  },

  setIdentity: (id) => {
    /*
     * Everything that belongs to a show has to go when the show does.
     *
     * rfEverLive is deliberately sticky within a session, which makes it
     * poison across one: a producer who closes a show with a bridge and opens
     * one without carries the flag over, and the second show is told at once
     * that it has lost a rig it never had -- in red, pulsing, advising them to
     * go and check hardware that is not in the building. The preview position
     * is just as wrong, pointing at a number in a running order that no longer
     * exists.
     */
    const changed = id?.code !== get().identity?.code;
    saveIdentity(id);
    set({
      identity: id,
      state: id ? get().state : null,
      ...(changed ? FRESH_SHOW : {}),
    });
    if (id) get().connect();
    else socket?.close();
  },

  setView: (view) => set({ view }),

  notify: (text, kind = 'info') => {
    set({ toast: { text, kind } });
    window.setTimeout(() => set({ toast: null }), 4000);
  },

  dismissJoinRequest: (id) =>
    set({ joinRequests: get().joinRequests.filter(p => p.id !== id) }),

  connect: () => {
    const id = get().identity;
    if (!id) return;
    const key = `${id.code}:${id.participantId}`;

    /*
     * Idempotent by design.
     *
     * Two callers race on sign-in: setIdentity connects, and App's mount effect
     * connects again when identity changes. Previously each call closed the
     * other's socket, and every close scheduled a reconnect that closed the
     * next one -- a permanent reconnect loop about once a second. It was easy
     * to miss because each cycle completed in milliseconds, but the server
     * re-broadcasts full session state on every connect, so one looping client
     * made every other client in the show re-render continuously too.
     */
    const live = socket
      && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING);
    if (live && socketKey === key) return;

    window.clearTimeout(retryTimer);
    if (socket) {
      // Detach handlers first: a socket we are deliberately replacing must not
      // schedule a reconnect on its way out.
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.close();
    }

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    socket = ws;
    socketKey = key;

    ws.onopen = () => {
      retry = 0;
      set({ connected: true });
      ws.send(JSON.stringify({ t: 'hello', code: id.code, participantId: id.participantId }));
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data as string);
      if (msg.t === 'state') set({ state: msg.state });
      else if (msg.t === 'conflicts') set({ conflicts: msg.conflicts });
      else if (msg.t === 'telemetry') {
        // Kept out of session state: meters change constantly, and folding them
        // in would re-render the whole board every second.
        set({
          telemetry: msg.readings,
          rfLive: msg.live,
          /*
           * A client that connects *during* an outage gets live:false and no
           * readings, but the accumulated history is still there -- and that
           * history is itself proof the bridge has fed this show. Without
           * counting it, refreshing the browser or joining from a second
           * device mid-outage leaves that screen convinced there was never a
           * rig, and it never says it has gone blind.
           */
          rfEverLive: get().rfEverLive || msg.live
            || Object.keys((msg as any).history ?? {}).length > 0,
          rfHistory: (msg as any).history ?? {},
          // Measured every push, so a slow drift never accumulates.
          clockSkewMs: typeof (msg as any).serverNow === 'number'
            ? (msg as any).serverNow - Date.now()
            : 0,
        });
      }
      else if (msg.t === 'join-request') {
        set({ joinRequests: [...get().joinRequests, msg.participant] });
      } else if (msg.t === 'session-ended') {
        get().notify('This show was ended by the host.', 'error');
        get().setIdentity(null);
      } else if (msg.t === 'error') {
        get().notify(msg.message, 'error');
      }
    };

    ws.onclose = () => {
      // Ignore a socket that has already been superseded.
      if (socket !== ws) return;
      set({ connected: false });
      // Reconnect with backoff -- a show laptop's wifi drops, and it must come back.
      window.clearTimeout(retryTimer);
      const delay = Math.min(1000 * 2 ** retry, 15_000);
      retry++;
      retryTimer = window.setTimeout(() => get().connect(), delay);
    };
  },
}));

/**
 * Selector helpers used across views.
 *
 * They share one frozen empty array rather than each writing `?? []`. Zustand
 * compares selector output by reference, so a fresh literal on every call looks
 * like a change every render and loops until React gives up with "maximum
 * update depth exceeded". It stayed hidden for a long time because every
 * consumer happened to mount after the first websocket state arrived; the first
 * component to render before it crashed immediately.
 */
const EMPTY: readonly never[] = Object.freeze([]);

export const usePresenters = () => useStore(s => s.state?.presenters ?? (EMPTY as never[]));
export const useChannels = () => useStore(s => s.state?.channels ?? (EMPTY as never[]));
export const useAssignments = () => useStore(s => s.state?.assignments ?? (EMPTY as never[]));
export const useCues = () => useStore(s => s.state?.cues ?? (EMPTY as never[]));
export const useMessages = () => useStore(s => s.state?.messages ?? (EMPTY as never[]));
export const useCanEdit = () =>
  useStore(s => s.identity?.role === 'host' || s.identity?.role === 'editor');

export const useTelemetry = () => useStore(s => s.telemetry);
export const useClockSkew = () => useStore(s => s.clockSkewMs);
export const useRfHistory = () => useStore(s => s.rfHistory);
export const useRfEverLive = () => useStore(s => s.rfEverLive);

/** The position this screen is showing: the show's, unless you have stepped away. */
export const useViewPos = () => useStore(s =>
  s.following ? (s.state?.session.playheadPos ?? 0) : s.previewPos);
export const useRfLive = () => useStore(s => s.rfLive);
